/**
 * Turning one extracted unit back into something readable (AB#5992).
 *
 * What ingest stores for a spreadsheet is the sheet flattened to delimited
 * rows, padding commas and all. Shown as monospace text it is a wall:
 * `Schrittketten Kreis 3,,,,,,,,,,,,,,,,,,,` is most of a line and none of the
 * information. The grid is what makes it legible again — and the grid is
 * recoverable, because the delimiters are still there.
 *
 * Whether to rebuild one is decided by the document's unit, not by the text.
 * Guessing from the text does not work in either direction: a German sentence
 * carries commas as a matter of course — "Der Druck stieg, wie erwartet, auf
 * 4,2 bar" — so any rule that counts them turns a PDF's paragraphs into a grid
 * and splits a decimal in half, while a real sheet's rows are too ragged for a
 * column-count rule to accept. The server already knows which it is.
 */

/** One block of a unit — the read endpoint joins its chunks with a blank line. */
export type UnitBlock =
  | {kind: 'table'; caption: string; rows: string[][]}
  | {kind: 'text'; caption: string; text: string}

/** e.g. `Sheet "Kreis 3", rows 2–4` — the locator ingest wrote for the chunk. */
const CAPTION = /^(Sheet|Page|Slide|Section)\b/

/** One column is a list, not a table. */
const MIN_TABLE_COLUMNS = 2

/**
 * Past this a unit is not something a person reads in a modal, and the grid
 * costs more to build than it is worth — one React component per cell. The
 * remaining rows are dropped rather than rendered.
 */
export const MAX_TABLE_ROWS = 2000

/** Split one delimited line, honouring quoted fields. */
export function splitRow(line: string, delimiter: string): string[] {
  const cells: string[] = []
  let cell = ''
  let quoted = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      // "" inside a quoted field is one literal quote.
      if (quoted && line[i + 1] === '"') {
        cell += '"'
        i++
      } else {
        quoted = !quoted
      }
      continue
    }
    if (char === delimiter && !quoted) {
      cells.push(cell.trim())
      cell = ''
      continue
    }
    cell += char
  }
  cells.push(cell.trim())
  return cells
}

/** Whichever delimiter actually divides these rows into columns. */
const delimiterOf = (lines: string[]): string | null => {
  for (const delimiter of ['\t', ',', ';']) {
    const widest = lines.reduce(
      (columns, line) => Math.max(columns, splitRow(line, delimiter).length),
      0,
    )
    if (widest >= MIN_TABLE_COLUMNS) return delimiter
  }
  return null
}

/**
 * Drop columns that are empty in every row.
 *
 * A sheet exported row-by-row carries the width of its widest row, so most
 * rows end in a run of empty cells that says nothing and costs the reader the
 * whole screen.
 */
const dropEmptyColumns = (rows: string[][]): string[][] => {
  // reduce, not Math.max(...spread): one argument per row overflows the call
  // stack somewhere north of a hundred thousand rows, and it would do it
  // inside a render.
  const width = rows.reduce((widest, row) => Math.max(widest, row.length), 0)
  const keep: number[] = []
  for (let col = 0; col < width; col++) {
    if (rows.some((row) => (row[col] ?? '').length > 0)) keep.push(col)
  }
  return rows.map((row) => keep.map((col) => row[col] ?? ''))
}

/**
 * @param tabular whether this document's units are rows — a sheet, not a page.
 *   Comes from the read response's `unit`; see {@link isTabularUnit}.
 */
export function parseUnitContent(content: string, tabular = false): UnitBlock[] {
  return content
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((block) => block.replace(/\s+$/, ''))
    .filter(Boolean)
    .map((block) => {
      const lines = block.split('\n')
      const caption = CAPTION.test(lines[0]) ? lines[0] : ''
      const body = caption ? lines.slice(1) : lines
      const meaningful = body.filter((l) => l.trim().length > 0)

      if (meaningful.length === 0) return {kind: 'text' as const, caption, text: ''}

      const delimiter = tabular ? delimiterOf(meaningful) : null
      if (!delimiter) {
        return {kind: 'text' as const, caption, text: meaningful.join('\n')}
      }

      const rows = dropEmptyColumns(
        meaningful.slice(0, MAX_TABLE_ROWS).map((l) => splitRow(l, delimiter)),
      )
      // Every cell empty once the padding is gone: nothing worth a grid.
      if (rows.every((row) => row.every((cell) => !cell))) {
        return {kind: 'text' as const, caption, text: meaningful.join('\n')}
      }
      return {kind: 'table' as const, caption, rows}
    })
}

/** Units that are rows. The registry's own word for them, straight from the API. */
export const isTabularUnit = (unit?: string): boolean => unit === 'sheet'

/**
 * The unit's own name, out of the locator ingest stored.
 *
 * `Sheet "Kreis 1", rows 2-4` names one thing to the reader: Kreis 1. The row
 * range belongs on the block it describes, not in the heading — the heading has
 * to match the link that was clicked, and the server builds that link from the
 * quoted name.
 */
export function shortUnitLabel(locator: string): string {
  const quoted = /"([^"]+)"/.exec(locator)
  return (quoted ? quoted[1] : locator).trim()
}
